import { Button } from '@flowus/common/components';
import { Input } from '@flowus/common/components/input';
import { cx } from '@flowus/common/cx';
import { useOpenModal } from '@flowus/common/next-modal';
import { useMemo, useState, type FC } from 'react';
import { useHistory } from 'react-router-dom';
import { Icon } from 'src/common/components/icon';
import { useUpload } from 'src/hooks/space/use-upload';
import { transaction } from 'src/hooks/use-transaction';
import { Images } from 'src/image';
import { updateBlock } from 'src/redux/managers/block/update';
import { WikiManager } from 'src/redux/managers/block/wiki-manager';
import { ViewPath } from 'src/utils';
import { v4 as uuidV4 } from 'uuid';
import type { WikiQuestion } from './type';

export const WIKI_PANEL_ID = 'wiki_panel_id';
const MAX_TEXTAREA_LENGTH = 200;
export const CreateWikiPanel: FC = () => {
  const openModal = useOpenModal();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<WikiQuestion[]>([
    {
      uuid: uuidV4(),
      question: '',
    },
  ]);
  const [file, setFile] = useState<File>();
  const url = useMemo(() => {
    if (file) {
      return URL.createObjectURL(file);
    }
  }, [file]);
  const upload = useUpload();
  const history = useHistory();
  return (
    <div className="w-[400px] px-6 py-5 flex flex-col">
      <div className="w-full text-center text-t1-medium text-normal">创建知识库</div>
      <div className="mt-5 text-t2">
        <span className="text-red">*</span>
        <span>名称</span>
      </div>
      <div className="relative my-1">
        <Input
          autoFocus
          value={title}
          maxLength={50}
          className="h-8 mt-0.5"
          placeholder="请输入知识库名称，例如：创作灵感"
          onChange={(v) => {
            setTitle(v);
          }}
        />
        <div className="absolute right-3 bottom-1 text-grey3 text-t5">
          {title.length}/{50}
        </div>
      </div>
      <div className="mt-4 text-t2">封面</div>
      <label className="relative w-fit cursor-pointer">
        <img className="mt-1 w-20 h-20" src={url ?? Images.wikiDefaultIcon} />
        <div className="absolute right-1 bottom-1 w-5 h-5 rounded-full flex items-center justify-center bg-black/50 ">
          <Icon name={'IcEdit02'} size="xxsmall" className="text-white" />
        </div>
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const fileList = e.target.files;
            if (!fileList || !fileList[0]) return;
            const file = fileList[0];
            setFile(file);
          }}
        />
      </label>
      <div className="mt-4 text-t2">描述</div>
      <div className="relative my-1">
        <textarea
          className="text-t2 py-1 px-2 w-full bg-grey9 rounded border border-grey6 focus-within-border"
          rows={4}
          placeholder="描述你的知识库，例如：收藏学习、日常收集整理"
          maxLength={MAX_TEXTAREA_LENGTH}
          onChange={(e) => setDescription(e.target.value)}
          style={{ resize: 'none' }}
        />
        <div className="absolute right-3 bottom-2 text-grey3 text-t5">
          {description.length}/{MAX_TEXTAREA_LENGTH}
        </div>
      </div>
      <div className="mt-4 text-t2 flex items-center justify-between">
        <div className="">设置推荐问题</div>
        <div
          className={cx('text-grey3 flex items-center ', {
            'opacity-40 cursor-pointer': questions.length >= 3,
            'animate-click': questions.length < 3,
          })}
          onClick={() => {
            if (questions.length >= 3) return;
            setQuestions([
              ...questions,
              {
                uuid: uuidV4(),
                question: '',
              },
            ]);
          }}
        >
          <Icon name="IcAdd" size="small" />
          <div>添加问题</div>
        </div>
      </div>
      <div className="space-y-1">
        {questions.map((item, index) => {
          return (
            <div className="relative my-2 group" key={index}>
              <Input
                value={item.question}
                onChange={(v) => {
                  const newQuestions = questions.map((q) => {
                    if (q.uuid === item.uuid) {
                      return { ...q, question: v };
                    }
                    return q;
                  });
                  setQuestions(newQuestions);
                }}
                key={index}
                className="h-8 mt-0.5"
                placeholder="为你的知识库预设推荐问题"
              />
              {questions.length !== 1 && (
                <Icon
                  className="hidden animate-click group-hover:block text-grey3 absolute right-2 bottom-1.5"
                  name={'IcTrash'}
                  size="middle"
                  onClick={() => {
                    setQuestions(questions.filter((q) => q.uuid !== item.uuid));
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
      <Button
        colorType="primary"
        className="mt-6 h-9"
        onClick={() => {
          openModal.closeModal(WIKI_PANEL_ID);
          const wikiId = WikiManager.createWiki(title, description);
          if (!wikiId) return;
          transaction(() => {
            updateBlock(wikiId, {
              data: {
                question: questions.map((q) => q.question).filter((v) => v),
              },
            });
          });
          if (file) {
            void upload({
              file,
              blockId: wikiId,
              type: 'file',
              onComplete(result) {
                transaction(() => {
                  result.success &&
                    updateBlock(wikiId, {
                      data: {
                        icon: {
                          type: 'upload',
                          value: result.ossName,
                        },
                      },
                    });
                });
              },
            });
          }
          history.push(`${ViewPath.wiki}/${wikiId}`);
        }}
      >
        确定
      </Button>
    </div>
  );
};
